import { getStyleByClass, removeHTMLTags, transpose } from "./helpers/main";
import { ICsvExportConfig, IGrid, IXlsxExportConfig } from "./types";
import { CsvDriver, TreeCollection } from "@dhx/ts-data";
import { downloadFile } from "@dhx/ts-common/core";
import { getFormattedDate, stringToDate } from "@dhx/ts-common/date";

function fillArray(arr: any[], value: any): any[] {
	for (let i = 0; i < arr.length; i++) {
		arr[i] = value;
	}
	return arr;
}

export class Exporter {
	constructor(private _view: IGrid) {}

	xlsx(config?: IXlsxExportConfig) {
		return this._export(config);
	}
	csv(config: ICsvExportConfig = {}) {
		config = {
			...{
				asFile: true,
				rowDelimiter: "\n",
				columnDelimiter: ",",
				skipHeader: 0,
			},
			...config,
		};
		let csv: string;
		if ("getRoot" in this._view.data && config.flat) {
			csv = this.getFlatCSV(config);
		} else {
			csv = this._getCSV(config);
		}
		const name = config.name || "grid_export";

		if (config.asFile) {
			downloadFile(csv, `${name}.csv`, "text/csv");
		}
		return csv;
	}
	private _export(config: IXlsxExportConfig = {}) {
		const configCols = this._view.config.columns.filter(i => !i.hidden);
		const rowsIndexMap = {};
		const headers = transpose(configCols.map(col => col.header.map(level => level.text || " ")));

		const columns = [];
		const uniqStyles = {
			default: {
				color: "#000000",
				background: "#FFFFFF",
				fontSize: 14,
			},
		};
		const cells = [];
		const columnsIndexMap = {};

		const data = this._view.data.serialize().map((row, i) => {
			rowsIndexMap[row.id] = i;
			const rowData = configCols.map((col, k) => {
				columnsIndexMap[col.id] = k;

				if (row[col.id] instanceof Date) {
					const format = col.format || "%M %d %Y";
					if (typeof row[col.id] === "string") {
						row[col.id] = getFormattedDate(format, stringToDate(row[col.id], format));
					} else if (typeof row[col.id] === "object") {
						row[col.id] = getFormattedDate(format, row[col.id]);
					}
				}

				return !row[col.id] && !k && row["$groupName"]
					? `^ ${row["$groupName"]}`
					: removeHTMLTags(row[col.id]);
			});
			return rowData;
		});
		const footer = [];
		const content = this._view.content;

		for (const col of configCols) {
			if (col.footer) {
				const id = col.id;
				const columnData = this._view.data.serialize().reduce((items: any[], item) => {
					if (item[id] !== undefined && item[id] !== "" && !isNaN(item[id])) {
						items.push(parseFloat(item[id]));
					}
					return items;
				}, []);
				let roots = columnData;
				if (this._view.config.type === "tree") {
					const datacollection = this._view.data as TreeCollection;
					const data = datacollection.serialize();
					roots = data.reduce((total, item) => {
						if (item.$level === 0) {
							if (item[id] !== undefined && item[id] !== "" && !isNaN(item[id])) {
								total.push(parseFloat(item[id]) || 0);
							} else {
								let value = 0;
								datacollection.eachChild(item.id, cell => {
									if (!datacollection.haveItems(cell.id)) {
										value += parseFloat(cell[id]);
									}
								});
								total.push(value);
							}
						}
						return total;
					}, []);
				}

				if (col.footer[0].content) {
					const val = content[col.footer[0].content].calculate(columnData, roots);
					footer.push(val.toString());
				} else {
					footer.push(col.footer[0].css || col.footer[0].text || " ");
				}
			}

			columns.push({ width: col.width });
			for (const key in col.$cellCss) {
				const colStyle = col.$cellCss[key];
				const colStyleHash = colStyle
					.split("")
					.reduce((h, letter) => {
						const hh = (h << 5) - h + letter.charCodeAt(0);
						return Math.abs(hh & hh);
					}, 0)
					.toString();

				if (!uniqStyles[colStyleHash]) {
					const cont = document.body;
					const css = getStyleByClass(colStyle, cont, "dhx_grid-row", uniqStyles.default);
					if (css) {
						uniqStyles[colStyleHash] = css;
					}
				}
				if (uniqStyles[colStyleHash]) {
					cells.push([rowsIndexMap[key], configCols.indexOf(col), colStyleHash]);
				}
			}
		}
		if (footer.length) {
			data.push(footer);
		}
		const exportData = {
			name: config.name || "data",
			columns,
			header: headers,
			data,
			styles: {
				cells,
				css: uniqStyles,
			},
		};

		if (config.url) {
			const form = document.createElement("form");
			form.setAttribute("target", "_blank");
			form.setAttribute("action", config.url);
			form.setAttribute("method", "POST");
			form.style.visibility = "hidden";

			const input = document.createElement("textarea");
			input.setAttribute("name", "data");
			input.value = JSON.stringify(exportData);

			form.appendChild(input);
			document.body.appendChild(form);
			form.submit();

			setTimeout(function() {
				form.parentNode.removeChild(form);
			}, 100);
		}
		return exportData;
	}
	private getFlatCSV(config: ICsvExportConfig) {
		const treeData: any = this._view.data;
		const root = treeData.getRoot();
		const firstCol = this._view.config.columns[0];

		const maxLevel = treeData.getMaxLevel();

		const getParentsChain = (item: any, data: any): string[] => {
			const parents = [];
			for (let i = 0; i <= maxLevel; i++) {
				if (item && item[firstCol.id]) {
					parents[item.$level] = item[firstCol.id];
					const parent = data.getParent(item.id, true);
					if (parent && parent.id) {
						item = parent;
					} else {
						item = null;
					}
				} else {
					parents[i] = "";
				}
			}

			return parents;
		};

		let total = "";
		treeData.eachChild(root, item => {
			const parents = getParentsChain(item, treeData).join(config.columnDelimiter);

			total +=
				parents +
				Object.keys(item).reduce((values, key, i) => {
					if (key !== "id" && key !== "parent" && !key.startsWith("$") && i !== 0) {
						return values + config.columnDelimiter + (item[key] === null ? "" : item[key]);
					}
					return values;
				}, "");

			total += config.rowDelimiter;
		});

		const exportData = this._export(config);

		// [dirty]
		const emptyHeaders = fillArray(new Array(maxLevel + 1), "");

		const headers = exportData.header.map(header => {
			header.splice(0, 1, ...emptyHeaders);
			return header;
		});

		const head = new CsvDriver(config).serialize(headers, true) + config.rowDelimiter;
		return head + total;
	}
	private _getCSV(config: ICsvExportConfig) {
		const exportData = this._export(config);
		const headers = exportData.header;

		const driver = new CsvDriver(config);

		const head = driver.serialize(headers, true);
		const readyData = driver.serialize(exportData.data, true);

		return head + "\n" + readyData;
	}
}
